// src/views/admin/ADNotes.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

//  Normalise la classe d'un élève
function getEleveClasse(eleve, anneeId) {
  if (eleve.classe_id !== undefined) {
    return { classeId: eleve.classe_id, classeNom: eleve.classe_nom || "Non assigné" };
  }
  const insc = (eleve.inscriptions || []).find(i => String(i.annee_scolaire) === String(anneeId));
  return {
    classeId: insc?.classe ?? null,
    classeNom: insc?.classe_nom ?? "Non assigné",
  };
}

//  Petit composant réutilisable pour les messages d'état (chargement / vide / info)
function InfoState({ icon, children, tone = "muted" }) {
  const color = tone === "warn" ? "#dc3545" : "var(--h)";
  return (
    <div className="card" style={{ textAlign: "center", color, padding: 40 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
      {children}
    </div>
  );
}

export default function ADNotes({ slug }) {
  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [loadingAnnees, setLoadingAnnees] = useState(true);

  const [plannifications, setPlannifications] = useState([]);
  const [plannifFilt, setPlannifFilt] = useState("");
  const [loadingPlannif, setLoadingPlannif] = useState(false);

  const [classeFilt, setClasseFilt] = useState("");
  const [showClassSelector, setShowClassSelector] = useState(false);
  const [matieres, setMatieres] = useState([]);
  const [notesMap, setNotesMap] = useState({});
  const [edits, setEdits] = useState({});

  const [loading, setLoading] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMatiere, setFilterMatiere] = useState("");

  //  État pour les élèves
  const [elevesClasse, setElevesClasse] = useState([]);

  // Import Excel (référence : SaisieNotesPage.jsx, backend import_notes_from_excel)
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  //  Refs des inputs de la grille, pour la navigation clavier (Entrée / flèches)
  const inputRefs = useRef({});

  const toast = useToastHook();
  const {
    getAnneesScolaires, getPlannifications,
    getMatieresParClasse, getNotes, bulkUpdateNotes,
    importerNotesExcel, 
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadPlannifications();
  }, [slug, anneeFilt]);

  useEffect(() => {
    //  Réinitialiser quand la plannification change
    setClasseFilt("");
    setShowClassSelector(false);
    setMatieres([]);
    setNotesMap({});
    setEdits({});
    setSearch("");
    setFilterMatiere("");
    setElevesClasse([]);
  }, [plannifFilt]);

  useEffect(() => {
    // Charger la grille quand la classe est sélectionnée
    if (plannifFilt && classeFilt && !showClassSelector) {
      loadGrid();
    }
  }, [plannifFilt, classeFilt, showClassSelector]);

  // Avertir avant de fermer/rafraîchir l'onglet s'il y a des modifications non enregistrées
  useEffect(() => {
    const handler = (e) => {
      if (Object.keys(edits).length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [edits]);

  const init = async () => {
    setLoading(true);
    setLoadingAnnees(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
      setLoadingAnnees(false);
    }
  };

  const loadPlannifications = async () => {
    setLoadingPlannif(true);
    try {
      const data = await getPlannifications(slug, { annee_scolaire: anneeFilt });
      setPlannifications(data);
      //  Sélectionner la première plannification par défaut
      if (data.length > 0) {
        setPlannifFilt(String(data[0].id));
      } else {
        setPlannifFilt("");
      }
    } catch (error) {
      toast.error("Impossible de charger les plannifications");
    } finally {
      setLoadingPlannif(false);
    }
  };

  //  Empêche de perdre des modifications non enregistrées en changeant de contexte
  const confirmDiscardChanges = () => {
    const nb = Object.keys(edits).length;
    if (nb === 0) return true;
    return window.confirm(
      `Vous avez ${nb} modification(s) non enregistrée(s) qui seront perdues. Voulez-vous continuer ?`
    );
  };

  const handlePlannifChange = (value) => {
    if (!confirmDiscardChanges()) return;
    setPlannifFilt(value);
  };

  const selectedPlannif = plannifications.find(p => String(p.id) === String(plannifFilt));

  // Classes disponibles
  const classesDisponibles = useMemo(() => {
    if (!selectedPlannif) return [];
    const detail = selectedPlannif.eleves_concernes_detail || [];
    const map = new Map();
    detail.forEach(e => {
      const { classeId, classeNom } = getEleveClasse(e, anneeFilt);
      if (classeId != null && !map.has(classeId)) {
        map.set(classeId, { id: classeId, nom: classeNom });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [selectedPlannif, anneeFilt]);

  //  Sélection automatique si une seule classe, sinon sélecteur plein écran
  useEffect(() => {
    if (classesDisponibles.length === 1) {
      setClasseFilt(String(classesDisponibles[0].id));
      setShowClassSelector(false);
    } else if (classesDisponibles.length > 1 && !classeFilt) {
      setShowClassSelector(true);
    }
  }, [classesDisponibles]);

  //  Élèves filtrés
  const elevesFiltres = useMemo(() => {
    return elevesClasse.filter(e => {
      const matchSearch = !search ||
        `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase()) ||
        e.matricule?.toLowerCase().includes(search.toLowerCase());
      return matchSearch;
    });
  }, [elevesClasse, search]);

  const matieresFiltrees = matieres.filter(m =>
    !filterMatiere || m.nom.toLowerCase().includes(filterMatiere.toLowerCase())
  );

  const loadGrid = async () => {
    if (!plannifFilt || !classeFilt) return;

    setLoadingGrid(true);
    setEdits({});
    try {
      //  Récupérer les élèves de la classe
      const detail = selectedPlannif?.eleves_concernes_detail || [];
      const eleves = detail.filter(e => {
        const { classeId } = getEleveClasse(e, anneeFilt);
        return String(classeId) === String(classeFilt);
      });
      setElevesClasse(eleves);

      //  Récupérer les matières
      const [matieresData, notesData] = await Promise.all([
        getMatieresParClasse(slug, { classe: classeFilt }),
        getNotes(slug, { periode: plannifFilt }),
      ]);
      setMatieres(matieresData);

      //  Construire la map des notes
      const eleveIds = new Set(eleves.map(e => e.id));
      const map = {};
      notesData
        .filter(n => eleveIds.has(n.eleve))
        .forEach(n => {
          map[`${n.eleve}_${n.matiere}`] = { id: n.id, valeur: n.valeur, appreciation: n.appreciation || "" };
        });
      setNotesMap(map);
    } catch (error) {
      toast.error("Impossible de charger la grille de notes");
      //  En cas d'erreur, on repart d'un état propre plutôt que de laisser un affichage incohérent
      setElevesClasse([]);
      setMatieres([]);
      setNotesMap({});
    } finally {
      setLoadingGrid(false);
    }
  };

  const cellValue = (eleveId, matiereId) => {
    const key = `${eleveId}_${matiereId}`;
    if (edits[key] !== undefined) return edits[key];
    return notesMap[key]?.valeur ?? "";
  };

  const handleCellChange = (eleveId, matiereId, value) => {
    if (value !== "" && (parseFloat(value) < 0 || parseFloat(value) > 20)) {
      toast.error("La note doit être entre 0 et 20");
      return;
    }
    const key = `${eleveId}_${matiereId}`;
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  //  Navigation clavier façon tableur : Entrée / flèche bas → cellule du dessous,
  // flèche haut → cellule du dessus (même matière, élève suivant/précédent).
  const handleCellKeyDown = (e, eleveIndex, matiereId) => {
    if (e.key !== "Enter" && e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const targetIndex = e.key === "ArrowUp" ? eleveIndex - 1 : eleveIndex + 1;
    const targetEleve = elevesFiltres[targetIndex];
    if (!targetEleve) return;
    const ref = inputRefs.current[`${targetEleve.id}_${matiereId}`];
    if (ref) {
      ref.focus();
      ref.select?.();
    }
  };

  const moyenneEleve = (eleveId) => {
    let total = 0, coefTotal = 0;
    matieresFiltrees.forEach(m => {
      const v = cellValue(eleveId, m.id);
      if (v !== "" && v !== null && v !== undefined) {
        total += parseFloat(v) * m.coefficient;
        coefTotal += m.coefficient;
      }
    });
    return coefTotal > 0 ? (total / coefTotal).toFixed(2) : "—";
  };

  const nbEdits = Object.keys(edits).length;

  const getStatsForMatiere = (matiereId) => {
    const notesMatiere = elevesClasse
      .map(e => cellValue(e.id, matiereId))
      .filter(n => n !== "" && n !== null && n !== undefined);
    if (notesMatiere.length === 0) return null;
    const notesValides = notesMatiere.map(n => parseFloat(n));
    const moyenne = notesValides.reduce((s, n) => s + n, 0) / notesValides.length;
    return {
      moyenne: moyenne.toFixed(2),
      max: Math.max(...notesValides).toFixed(2),
      min: Math.min(...notesValides).toFixed(2),
      count: notesValides.length
    };
  };

  const getGlobalStats = () => {
    const allNotes = Object.values(notesMap).filter(n => n.valeur !== "" && n.valeur !== null && n.valeur !== undefined);
    if (allNotes.length === 0) return null;
    const notesValides = allNotes.map(n => parseFloat(n.valeur));
    const moyenne = notesValides.reduce((s, n) => s + n, 0) / notesValides.length;
    const totalPossibles = elevesClasse.length * matieres.length;
    return {
      moyenne: moyenne.toFixed(2),
      max: Math.max(...notesValides).toFixed(2),
      min: Math.min(...notesValides).toFixed(2),
      total: allNotes.length,
      totalEdits: nbEdits,
      percentage: totalPossibles > 0 ? ((allNotes.length / totalPossibles) * 100).toFixed(1) : "0"
    };
  };

  const handleSaveAll = async () => {
    if (nbEdits === 0) { toast.info("Aucune modification à enregistrer"); return; }

    const invalides = [];
    const notesPayload = Object.entries(edits).map(([key, value]) => {
      const [eleveIdStr, matiereIdStr] = key.split("_");
      const eleveId = parseInt(eleveIdStr);
      const matiereId = parseInt(matiereIdStr);
      if (value !== "" && (parseFloat(value) < 0 || parseFloat(value) > 20)) {
        invalides.push(key);
      }
      return {
        periode: plannifFilt,
        eleve: eleveId,
        matiere: matiereId,
        valeur: value === "" ? null : parseFloat(value),
        appreciation: notesMap[key]?.appreciation || "",
      };
    });

    if (invalides.length > 0) {
      toast.error("Certaines notes ne sont pas entre 0 et 20");
      return;
    }

    setSaving(true);
    try {
      const res = await bulkUpdateNotes(slug, notesPayload);
      toast.success(res.message || "Notes enregistrées avec succès");
      await loadGrid();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.error || "Erreur lors de la sauvegarde des notes");
    } finally {
      setSaving(false);
    }
  };

  const handleClassSelection = (classeId) => {
    setClasseFilt(String(classeId));
    setShowClassSelector(false);
    setSearch("");
  };

  const handleChangeClass = () => {
    if (!confirmDiscardChanges()) return;
    setShowClassSelector(true);
    setClasseFilt("");
    setElevesClasse([]);
    setMatieres([]);
    setNotesMap({});
    setEdits({});
    setSearch("");
  };

  // ─── Import Excel ───
  // Référence : bouton "Importer depuis Excel" de SaisieNotesPage.jsx, implémenté
  // ici en modal d'upload direct (POST multipart: file + periode) sur la route
  // notes/import-excel/.  Corrigé : le bouton renvoyait vers une autre page au
  // lieu d'ouvrir cette modale déjà codée — il l'ouvre maintenant correctement.
  const openImportModal = () => {
    if (!plannifFilt) {
      toast.error("Sélectionnez d'abord une plannification");
      return;
    }
    setImportFile(null);
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportFile(null);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      //  Validation du fichier avant envoi (extension + taille)
      const validExt = /\.(xlsx|xls)$/i.test(file.name);
      if (!validExt) {
        toast.error("Le fichier doit être au format .xlsx ou .xls");
        e.target.value = "";
        setImportFile(null);
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5 Mo
      if (file.size > maxSize) {
        toast.error("Le fichier est trop volumineux (5 Mo maximum)");
        e.target.value = "";
        setImportFile(null);
        return;
      }
    }
    setImportFile(file);
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error("Sélectionne un fichier Excel (.xlsx)");
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("periode", plannifFilt);

      const res = await importerNotesExcel(slug, formData);
      toast.success(res.message || "Import réussi");
      setShowImportModal(false);
      setImportFile(null);
      await loadGrid();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.error || "Erreur lors de l'import du fichier");
    } finally {
      setImporting(false);
    }
  };

  // ─── PDF ───
  const generatePDF = () => {
    if (!selectedPlannif || !elevesClasse.length || !matieres.length) {
      toast.error("Impossible de générer le PDF");
      return;
    }

    if (nbEdits > 0) {
      toast.info("Le PDF inclut des modifications non encore enregistrées");
    }

    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = 297;
      const pageHeight = 210;
      const currentDate = new Date().toLocaleDateString("fr-FR");
      const classeNom = classesDisponibles.find(c => String(c.id) === String(classeFilt))?.nom || "";

      doc.setFontSize(19);
      doc.setTextColor(30, 60, 114);
      doc.text("FEUILLE DE NOTES", pageWidth / 2, 16, { align: "center" });
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(
        `${selectedPlannif.trimestre_detail?.nom || "TRIMESTRE"} - ${selectedPlannif.nom} - ${classeNom}`,
        pageWidth / 2, 25, { align: "center" }
      );
      doc.setFontSize(13);
      doc.setTextColor(30, 60, 114);
      doc.text("NOTES DES ÉLÈVES", pageWidth / 2, 34, { align: "center" });

      const leftMargin = 8, rightMargin = 8;
      const fixedWidth = 10 + 26 + 48;
      const availableWidth = pageWidth - leftMargin - rightMargin - fixedWidth;
      const matiereWidth = Math.max(10, availableWidth / matieres.length);

      const headers = ["N°", "Matricule", "Nom & Prénom", ...matieres.map(m => m.nom.length > 10 ? m.nom.substring(0, 10) + "." : m.nom)];
      const body = elevesClasse.map((e, i) => {
        const row = [i + 1, e.matricule || "—", `${e.nom} ${e.prenom}`];
        matieres.forEach(m => {
          const v = cellValue(e.id, m.id);
          row.push(v ? Number(v).toFixed(2) : "");
        });
        return row;
      });

      autoTable(doc, {
        startY: 40,
        theme: "grid",
        margin: { left: leftMargin, right: rightMargin },
        head: [headers],
        body,
        styles: { fontSize: 7.5, cellPadding: 2, valign: "middle" },
        headStyles: { fillColor: [30, 60, 114], textColor: 255, fontSize: 7.5, halign: "center" },
        columnStyles: (() => {
          const s = {
            0: { cellWidth: 10, halign: "center" },
            1: { cellWidth: 26, halign: "center" },
            2: { cellWidth: 48, halign: "left" }
          };
          matieres.forEach((_, idx) => {
            s[idx + 3] = { cellWidth: matiereWidth, halign: "center" };
          });
          return s;
        })(),
        pageBreak: "avoid",
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text(`Page 1 - Généré le ${currentDate}`, pageWidth / 2, pageHeight - 8, { align: "center" });
        }
      });

      doc.save("FEUILLE_DE_NOTES.pdf");
      toast.success("PDF généré avec succès");
    } catch (error) {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  if (loading) return <Loader />;

  const globalStats = getGlobalStats();

  return (
    <div>

      {/* Aucune année scolaire configurée du tout */}
      {!loadingAnnees && annees.length === 0 && (
        <InfoState icon="ti-calendar-off" tone="warn">
          Aucune année scolaire n'est configurée. Créez-en une avant de saisir des notes.
        </InfoState>
      )}

      {annees.length > 0 && (
        <>
          {/* Sélection de la plannification */}
          <div className="planif-selector">
            <div className="planif-selector-header">
              <i className="ti ti-calendar" />
              <h3>Sélectionnez une plannification</h3>
            </div>
            <div className="planif-selector-body">
              <select
                className="planif-select"
                value={plannifFilt}
                onChange={e => handlePlannifChange(e.target.value)}
                disabled={loadingPlannif || plannifications.length === 0}
              >
                <option value="">-- Sélectionner une plannification --</option>
                {plannifications.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nom} — {p.trimestre_detail?.nom || "Sans trimestre"}
                  </option>
                ))}
              </select>
              {plannifFilt && (
                <span className="planif-selector-info">
                  <i className="ti ti-info-circle" /> Saisie des notes pour la plannification sélectionnée
                </span>
              )}
            </div>
          </div>

          {loadingPlannif && (
            <InfoState icon="ti-loader">Chargement des plannifications...</InfoState>
          )}

          {!loadingPlannif && plannifications.length === 0 && (
            <InfoState icon="ti-calendar-off">
              Aucune plannification (période d'évaluation) n'existe pour cette année scolaire.
              Créez-en une avant de pouvoir saisir des notes.
            </InfoState>
          )}

          {/* Infos plannification */}
          {plannifFilt && selectedPlannif && (
            <div className="card planif-info">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <h3 style={{ margin: 0, color: "var(--pr)" }}>{selectedPlannif.nom}</h3>
                  <div style={{ fontSize: 13, color: "var(--m)", marginTop: 4 }}>
                    <strong>Trimestre:</strong> {selectedPlannif.trimestre_detail?.nom || "—"}
                    {selectedPlannif.sous_trimestre_detail && (
                      <> · <strong>Sous-trimestre:</strong> {selectedPlannif.sous_trimestre_detail.nom}</>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--m)" }}>
                    <strong>Élèves total:</strong> {selectedPlannif.eleves_concernes_detail?.length || 0}
                    {classeFilt && !showClassSelector && (
                      <> · <strong>Classe:</strong> {classesDisponibles.find(c => String(c.id) === String(classeFilt))?.nom}</>
                    )}
                    {classeFilt && !showClassSelector && (
                      <> · <strong>Élèves:</strong> {elevesClasse.length}</>
                    )}
                    {matieres.length > 0 && (
                      <> · <strong>Matières:</strong> {matieres.length}</>
                    )}
                    {globalStats && (
                      <> · <strong>Notes saisies:</strong> {globalStats.total} ({globalStats.percentage}%)</>
                    )}
                    {nbEdits > 0 && (
                      <span style={{ color: "var(--pr)", fontWeight: 600, marginLeft: 8 }}>
                        · {nbEdits} modification(s) en attente
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {classesDisponibles.length > 1 && !showClassSelector && (
                    <button className="bsec" onClick={handleChangeClass}>
                      <i className="ti ti-users" /> Changer de classe
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {plannifFilt && selectedPlannif && selectedPlannif.eleves_concernes_detail?.length === 0 && (
            <InfoState icon="ti-users">
              Aucun élève n'est assigné à cette plannification.
            </InfoState>
          )}

          {plannifFilt && selectedPlannif && selectedPlannif.eleves_concernes_detail?.length > 0 && classesDisponibles.length === 0 && (
            <InfoState icon="ti-school">
              Aucune classe trouvée pour les élèves de cette plannification.
            </InfoState>
          )}

          {/* Sélecteur de classe plein écran (plusieurs classes) */}
          {plannifFilt && showClassSelector && classesDisponibles.length > 1 && (
            <div className="card class-selector">
              <h3 style={{ color: "var(--pr)", marginBottom: 10 }}>
                <i className="ti ti-users" /> Sélectionnez une classe
              </h3>
              <p style={{ marginBottom: 24, color: "var(--m)", fontSize: 15 }}>
                Cette plannification contient des élèves de plusieurs classes. Veuillez sélectionner une classe pour saisir les notes.
              </p>
              <div className="class-grid">
                {classesDisponibles.map(classe => {
                  const nbEleves = (selectedPlannif?.eleves_concernes_detail || []).filter(e => {
                    const { classeId } = getEleveClasse(e, anneeFilt);
                    return String(classeId) === String(classe.id);
                  }).length;
                  return (
                    <button
                      key={classe.id}
                      className="class-card"
                      onClick={() => handleClassSelection(classe.id)}
                    >
                      <i className="ti ti-chalkboard" style={{ fontSize: 32, marginBottom: 8, display: "block" }} />
                      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{classe.nom}</div>
                      <div style={{ fontSize: 13 }}>{nbEleves} élève(s)</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 13, color: "var(--m)", marginTop: 20 }}>
                Vous pourrez changer de classe à tout moment pendant la saisie.
              </div>
            </div>
          )}

          {/* États de la grille clarifiés : chargement / pas d'élèves / pas de matières / grille prête.
              Avant, ces trois cas étaient confondus et pouvaient laisser un écran vide sans explication. */}
          {plannifFilt && !showClassSelector && classeFilt && (
            loadingGrid ? (
              <InfoState icon="ti-loader">Chargement des données de la classe...</InfoState>
            ) : elevesClasse.length === 0 ? (
              <InfoState icon="ti-users">
                Aucun élève n'est inscrit dans cette classe pour cette plannification.
              </InfoState>
            ) : matieres.length === 0 ? (
              <InfoState icon="ti-book-off" tone="warn">
                Aucune matière n'est configurée pour cette classe. Configurez les matières et leurs
                coefficients avant de pouvoir saisir des notes.
              </InfoState>
            ) : (
              <>
                {/* Filtres */}
                <div className="notes-filters">
                  <div className="notes-filter-field">
                    <label><i className="ti ti-search" /> Élève :</label>
                    <input
                      type="text"
                      placeholder="Nom, prénom ou matricule..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="notes-filter-field">
                    <label><i className="ti ti-filter" /> Matière :</label>
                    <input
                      type="text"
                      placeholder="Rechercher une matière..."
                      value={filterMatiere}
                      onChange={e => setFilterMatiere(e.target.value)}
                    />
                  </div>
                  <div className="notes-filter-summary">
                    <span>{elevesFiltres.length} élève(s) · {matieresFiltrees.length} matière(s)</span>
                    {(search || filterMatiere) && (
                      <button className="notes-filter-clear" onClick={() => { setSearch(""); setFilterMatiere(""); }}>
                        <i className="ti ti-x" /> Effacer filtres
                      </button>
                    )}
                  </div>
                </div>

                {elevesFiltres.length === 0 && search ? (
                  <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--h)", fontStyle: "italic" }}>
                    <i className="ti ti-search" style={{ marginRight: 8 }} />
                    Aucun élève ne correspond à « {search} »
                  </div>
                ) : (
                  <>
                    {/* Grille */}
                    <div className="notes-table-wrap">
                      <table className="notes-table">
                        <thead>
                          <tr>
                            <th style={{ width: 250, minWidth: 250, maxWidth: 300, textAlign: "left", position: "sticky", left: 0, zIndex: 20, background: "#004080" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="ti ti-user" /> Élève
                              </div>
                            </th>
                            {matieresFiltrees.map(m => {
                              const stats = getStatsForMatiere(m.id);
                              return (
                                <th
                                  key={m.id}
                                  style={{ width: 120, minWidth: 120, textAlign: "center" }}
                                  title={m.nom}
                                >
                                  <div className="matiere-nom">{m.nom}</div>
                                  <div style={{ fontWeight: 400, fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                                    Coef: {m.coefficient}
                                  </div>
                                  {stats && (
                                    <div style={{ fontWeight: 400, fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
                                      <i className="ti ti-chart-line" /> Moy: {stats.moyenne}
                                    </div>
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {elevesFiltres.map((e, eleveIndex) => {
                            return (
                              <tr key={e.id}>
                                <td style={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 10,
                                  width: 250,
                                  minWidth: 250,
                                  maxWidth: 300,
                                  textAlign: "left"
                                }}>
                                  <div style={{ fontWeight: 600 }}>{e.nom} {e.prenom}</div>
                                  <div style={{ fontSize: 12, color: "#666" }}>{e.matricule}</div>
                                </td>
                                {matieresFiltrees.map(m => {
                                  const key = `${e.id}_${m.id}`;
                                  const val = cellValue(e.id, m.id);
                                  const hasSaisie = val !== "" && val !== null && val !== undefined;
                                  const isDirty = edits[key] !== undefined;
                                  return (
                                    <td key={m.id} style={{ textAlign: "center", padding: 6, width: 120, minWidth: 120 }}>
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                        <input
                                          ref={el => { inputRefs.current[key] = el; }}
                                          type="number"
                                          min="0"
                                          max="20"
                                          step="0.25"
                                          value={val}
                                          onChange={ev => handleCellChange(e.id, m.id, ev.target.value)}
                                          onKeyDown={ev => handleCellKeyDown(ev, eleveIndex, m.id)}
                                          className={`note-input ${hasSaisie ? 'has-saisie' : ''} ${isDirty ? 'is-dirty' : ''}`}
                                          placeholder="0.00"
                                          aria-label={`Note de ${e.nom} ${e.prenom} en ${m.nom}`}
                                        />
                                        <div className={`note-status ${hasSaisie ? 'saisie' : 'non-saisie'}`}>
                                          {hasSaisie ? (
                                            <><i className="ti ti-check" /> Saisie</>
                                          ) : (
                                            <><i className="ti ti-x" /> À saisir</>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Barre de sauvegarde sticky */}
                    <div className="save-bar">
                      <div className="save-bar-actions">
                        <button
                          className="bsec"
                          onClick={generatePDF}
                          style={{ color: "#dc3545", borderColor: "#dc3545" }}
                        >
                          <i className="ti ti-file-pdf" /> Générer en fichier
                        </button>
                        <button
                          className="bsec"
                          onClick={openImportModal}
                          style={{ color: "#ff9800", borderColor: "#ff9800" }}
                        >
                          <i className="ti ti-file-spreadsheet" /> Importer depuis Excel
                        </button>
                        <button
                          className="bprim"
                          onClick={handleSaveAll}
                          disabled={saving || nbEdits === 0}
                        >
                          {saving ? (
                            <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Sauvegarde en cours...</>
                          ) : (
                            <><i className="ti ti-device-floppy" /> Sauvegarder les notes {nbEdits > 0 ? `(${nbEdits})` : ""}</>
                          )}
                        </button>
                      </div>
                      <div className="save-bar-summary">
                        {nbEdits > 0 && (
                          <span className="save-bar-pending">
                            <i className="ti ti-info-circle" /> {nbEdits} modification(s) en attente
                          </span>
                        )}
                        {globalStats ? (
                          <>{globalStats.total} notes saisies sur {elevesClasse.length * matieres.length} possibles</>
                        ) : (
                          <>0 note saisie sur {elevesClasse.length * matieres.length} possibles</>
                        )}
                        {search && ` · filtre élève actif : "${search}"`}
                      </div>
                    </div>
                  </>
                )}
              </>
            )
          )}
        </>
      )}

      {/* Modal Import Excel */}
      {showImportModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeImportModal()}>
          <div className="modal-box" style={{ maxWidth: 460 }}>
            <div className="modal-title">
              Importer les notes depuis Excel
              <button className="ibtn" style={{ border: "none" }} onClick={closeImportModal} disabled={importing}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 13, color: "var(--m)", margin: 0 }}>
                Sélectionne un fichier Excel (.xlsx, max 5 Mo) contenant les notes de la plannification
                <strong> {selectedPlannif?.nom}</strong>.
              </p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                disabled={importing}
              />
              {importFile && (
                <div style={{ fontSize: 12.5, color: "var(--m)" }}>
                  <i className="ti ti-file-spreadsheet" /> {importFile.name}
                </div>
              )}
            </div>
            <div className="factions">
              <button className="bsec" onClick={closeImportModal} disabled={importing}>Annuler</button>
              <button className="bprim" onClick={handleImportExcel} disabled={importing || !importFile}>
                {importing ? (
                  <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Import en cours...</>
                ) : (
                  <><i className="ti ti-upload" /> Importer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ─── SÉLECTEUR PLANIFICATION / ANNÉE ─── */
        .planif-selector {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          padding: 16px 20px;
          margin-bottom: 16px;
        }

        .planif-selector-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .planif-selector-header i {
          font-size: 24px;
          color: var(--pr);
        }

        .planif-selector-header h3 {
          margin: 0;
          font-size: 16px;
          color: var(--pr);
        }

        .planif-selector-body {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .planif-select {
          padding: 8px 16px;
          border: 0.5px solid var(--b2);
          border-radius: 6px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
          min-width: 300px;
        }

        .planif-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .planif-selector-info {
          font-size: 14px;
          color: var(--m);
        }

        .planif-selector-info i {
          margin-right: 4px;
        }

        /* ─── INFOS PLANIFICATION ─── */
        .planif-info {
          margin-bottom: 16px;
        }

        /* ─── SÉLECTEUR DE CLASSE PLEIN ÉCRAN ─── */
        .class-selector {
          text-align: center;
          padding: 32px 24px;
          margin-bottom: 16px;
        }

        .class-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }

        .class-card {
          background: var(--bg);
          border: 2px solid var(--pr);
          border-radius: 10px;
          padding: 20px;
          cursor: pointer;
          text-align: center;
          color: var(--t);
          transition: all 0.2s ease;
        }

        .class-card:hover {
          background: var(--pr);
          color: #fff;
        }

        /* ─── BARRE DE FILTRES (élève / matière) ───
           Reprend le format label + icône de l'ancienne page de saisie. */
        .notes-filters {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .notes-filter-field {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .notes-filter-field label {
          font-weight: 600;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--t);
        }

        .notes-filter-field input {
          padding: 8px 10px;
          border-radius: 6px;
          border: 0.5px solid var(--b2);
          background: var(--s);
          color: var(--t);
          font-size: 14px;
          width: 220px;
        }

        .notes-filter-summary {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
          font-size: 13px;
          color: var(--m);
        }

        .notes-filter-clear {
          background: none;
          border: none;
          color: var(--rd);
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        /* ─── TABLE NOTES ───
           Valeurs reprises telles quelles de l'ancien SaisieNotesPageStyles.js
           (bordures, couleurs, paddings) plutôt que des variables de thème. */
        .notes-table-wrap {
          overflow-x: auto;
          border-radius: 8px;
          border: 1px solid #e1e5eb;
          position: relative;
        }

        .notes-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          min-width: 800px;
        }

        .notes-table thead tr {
          background: #004080;
          color: #fff;
        }

        .notes-table th {
          padding: 15px 12px;
          text-align: center;
          font-weight: 600;
          font-size: 14px;
          border-right: 1px solid #003366;
        }

        .notes-table td {
          padding: 12px 10px;
          text-align: center;
          border-right: 1px solid #e1e5eb;
        }

        .notes-table tbody tr {
          border-bottom: 1px solid #e1e5eb;
        }

        .notes-table th:first-child,
        .notes-table td:first-child {
          position: sticky;
          left: 0;
          z-index: 10;
          box-shadow: 2px 0 5px rgba(0,0,0,0.08);
        }

        .notes-table thead th:first-child {
          z-index: 20;
          background: #004080;
        }

        /* ─── LIGNES ZÉBRÉES ─── */
        .notes-table tbody tr:nth-child(odd) td {
          background: #fff;
        }

        .notes-table tbody tr:nth-child(even) td {
          background: #f8f9fa;
        }

        /* ─── NOM DE MATIÈRE (en-tête) ───
           Empêche un nom trop long de déborder ou de casser
           la mise en page des colonnes à largeur fixe. Le nom
           complet reste visible via l'attribut title (tooltip). */
        .matiere-nom {
          font-weight: 600;
          font-size: 14px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
          padding: 0 4px;
        }

        /* ─── INPUT NOTE ─── */
        .note-input {
          width: 100px;
          padding: 10px;
          border-radius: 6px;
          border: 2px solid #ddd;
          text-align: center;
          font-size: 14px;
          font-weight: 600;
          background: #fff;
          transition: all 0.3s ease;
        }

        .note-input:focus {
          border-color: #004080;
          outline: none;
        }

        .note-input.has-saisie {
          border-color: #28a745;
          background: #f8fff9;
        }

        .note-input.is-dirty {
          border-color: #004080;
          background: #eef4fb;
        }

        /* ─── STATUT NOTE ─── */
        .note-status {
          font-size: 10px;
          display: flex;
          align-items: center;
          gap: 3px;
          font-weight: 600;
        }

        .note-status.saisie {
          color: #28a745;
        }

        .note-status.non-saisie {
          color: #dc3545;
        }

        .note-status i {
          font-size: 11px;
        }

        /* ─── BARRE DE SAUVEGARDE ─── */
        .save-bar {
          position: sticky;
          bottom: 0;
          background: var(--s);
          border-top: 3px solid var(--pr);
          padding: 15px 18px;
          margin-top: 16px;
          text-align: center;
          border-radius: 8px 8px 0 0;
          box-shadow: 0 -2px 15px rgba(0,0,0,0.15);
          z-index: 50;
        }

        .save-bar-actions {
          display: flex;
          justify-content: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .save-bar-summary {
          width: 100%;
          margin-top: 8px;
          font-size: 13px;
          color: var(--m);
        }

        .save-bar-pending {
          color: var(--pr);
          font-weight: 600;
          margin-right: 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        @media (max-width: 768px) {
          .planif-select {
            min-width: 200px;
          }
          .save-bar-actions {
            justify-content: center;
          }
          .notes-filter-summary {
            margin-left: 0;
          }
          .notes-filter-field input {
            width: 160px;
          }
          .note-input {
            width: 70px;
            padding: 6px;
          }
        }
      `}</style>
    </div>
  );
}